/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: menus.js
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 01/10/2025 - 16:58
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import * as React from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import * as utils from "../utils/utils.tsx";

import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import Settings from "@mui/icons-material/Settings";
import ManageAccounts from "@mui/icons-material/ManageAccounts";
import Logout from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditIcon from "@mui/icons-material/Edit";

import * as util from "../utils/utils.tsx";

/**
 * Navigation bar component, displays the top navigation menu
 *
 * @param {list<str>} actions The actions to display in the bar
 * @param {callback} onAction the callback to handle the selected action
 */
export default function NavBar({ actions, accountActions, curr_action, onSetCurrentAction, onSetIsLoggedIn }) {
   const [anchorElUser, setAnchorElUser] = React.useState(null);
   const [anchorEl, setAnchorEl] = React.useState(null);

   // Create callbacks to set the App state
   const setCurrentAction = React.useCallback(
      (action) => {
         if (action === util.AccountAction.Logout) {
            handleLogout();
         }
         onSetCurrentAction(action);
         setAnchorElUser(null);
         setAnchorEl(null);
      },
      [onSetCurrentAction, onSetIsLoggedIn]
   );

   // Callback for when logout is clicked
   const setIsLoggedIn = React.useCallback(
      (action) => {
         onSetIsLoggedIn(action);
      },
      [onSetIsLoggedIn]
   );

   // Remove connexion from front-end and back-end
   async function handleLogout() {
      // Remove token before sending reply, so that if it fails the user will not
      // be considered connected anyways
      try {
         await util.apiClient.post("/logout");
         localStorage.removeItem("token");
         setIsLoggedIn(false);
         toast.success("Au revoir !", { autoClose: 1000 });
      } catch (error) {
         toast.error(error);
      }
   }

   // function populateMenu() {
   // Build the actions and menus
   const displayAction = [];
   for (let index = 0; index < actions.length; index++) {
      // Menu: '/' found, agregate the actions in a single sub-menu
      if (actions[index].includes("/")) {
         const menuName = actions[index].split("/")[0];
         const id = index; // Use a const, as the value is otherwise overriden because passed as reference
         const values = [
            <MenuItem key={index} onClick={() => setCurrentAction(actions[id])}>
               {util.actionDisplayString(actions[index].split("/")[1])}
            </MenuItem>,
         ];
         while (
            index + 1 < actions.length &&
            actions[index + 1].split("/")[0] == menuName &&
            actions[index + 1].includes("/")
         ) {
            index++;
            const id = index;
            values.push(
               <MenuItem key={index} onClick={() => setCurrentAction(actions[id])}>
                  {util.actionDisplayString(actions[index].split("/")[1])}
               </MenuItem>
            );
         }

         displayAction.push(
            <div key={index}>
               <Button
                  className={
                     actions[index].split("/")[0] == curr_action.split("/")[0] ? "nav-link-selected" : "nav-link"
                  }
                  style={{ display: "flex" }}
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  endIcon={<KeyboardArrowDownIcon />}
               >
                  {util.actionDisplayString(menuName)}
               </Button>
               <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                  {values}
               </Menu>
            </div>
         );
      }

      // Single action -> button
      else {
         displayAction.push(
            <Grid key={index}>
               <Button
                  className={actions[index] == curr_action ? "nav-link-selected" : "nav-link"}
                  key={actions[index]}
                  onClick={() => setCurrentAction(actions[index])}
               >
                  {util.actionDisplayString(actions[index])}
               </Button>
            </Grid>
         );
      }
   }

   // function populateAccountMenu() {
   const displayAccountAction = [];
   for (let index = 0; index < accountActions.length; index++) {
      let logo = <Settings />;
      if (accountActions[index] === utils.AccountAction.Logout) logo = <Logout />;
      else if (accountActions[index] === utils.AccountAction.SetPasswd) logo = <EditIcon />;
      else if (accountActions[index] === utils.AccountAction.AdminPage) logo = <ManageAccounts />;

      displayAccountAction.push(
         <MenuItem key={index}>
            <ListItemIcon>{logo}</ListItemIcon>
            <Typography sx={{ textAlign: "left" }} onClick={() => setCurrentAction(accountActions[index])}>
               {util.actionDisplayString(accountActions[index])}
            </Typography>
         </MenuItem>
      );
   }

   return (
      <AppBar position="static" sx={{ bgcolor: "white" }} style={{ width: "100vw" }}>
         <Toolbar>
            <Box
               component="img"
               sx={{
                  flexGrow: 0,
                  height: 70,
                  width: 70,
                  maxHeight: { xs: 233, md: 167 },
                  maxWidth: { xs: 350, md: 250 },
               }}
               alt="E2RH tools"
               src="./images/logo-e2rh.svg"
            />
            <Grid
               container={true}
               spacing={5}
               direction="row"
               alignItems="center"
               justifyContent="center"
               sx={{ flexGrow: 1 }}
            >
               {displayAction}
            </Grid>
            <Box sx={{ flexGrow: 0 }}>
               <Tooltip title="Ouvrir le Menu de Compte">
                  <IconButton onClick={(event) => setAnchorElUser(event.currentTarget)}>
                     <Avatar alt="account-logo" src="/images/account.png" />
                  </IconButton>
               </Tooltip>
               <Menu
                  sx={{ mt: "45px" }}
                  id="account-menu"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                     vertical: "top",
                     horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                     vertical: "top",
                     horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={() => setAnchorElUser(null)}
               >
                  {displayAccountAction}
               </Menu>
            </Box>
         </Toolbar>
      </AppBar>
   );
}

NavBar.propTypes = {
   actions: PropTypes.arrayOf(PropTypes.string),
   accountActions: PropTypes.arrayOf(PropTypes.string),
   curr_action: PropTypes.string,
   onSetCurrentAction: PropTypes.func,
   onSetIsLoggedIn: PropTypes.func,
};
